import React, { useState } from "react";
import { Text, Container } from "components/common";
import { useMutation } from "@apollo/react-hooks";
import {
  SEND_VERIFICATION_CODE_WITH_RATE_LIMIT, // with rate limiting
  CHECK_VERIFICATION_CODE_WITH_RATE_LIMIT, // with rate limiting
} from "./mutations";
import VerificationCodeCheckScreen from "./VerificationCodeCheckScreen";

const PHONE_VERIFICATION_INCORRECT_MESSAGE = "⚠️ Verification code is incorrect";
const PHONE_VERIFICATION_LAST_ATTEMPT_MESSAGE = "1 attempt remaining before you can try again in 24 hours";
const PHONE_VERIFICATION_INVALID_MESSAGE = "Please enter a valid verification code";
const RESEND_CODE_DELAY_SECOND = 30;
const VERIFICATION_CODE_LENGTH = 5;

const TwoFactorVerification = ({
  user,
  isMobile,
  verifyMethods = ["email", "sms"],
  onVerified = () => {}
}) => {
  const [verificationMethod, setVerificationMethod] = useState(""); // 'email' or 'phone'
  const [verificationCode, setVerificationCode] = useState("");
  const [message, setMessage] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // function to start the countdown for resend button enablement
  const startResendCountdown = () => {
    setResendDisabled(true);
    setCountdown(RESEND_CODE_DELAY_SECOND);

    const countdownInterval = setInterval(() => {
      setCountdown((prevCountdown) => {
        const newCountdown = prevCountdown - 1;
        if (newCountdown === 0) {
          clearInterval(countdownInterval);
          setResendDisabled(false);
        }
        return newCountdown;
      });
    }, 1000);
  };

  const TFA_CONFIG = {
    sms: {
      title: "Text a code",
      recipient: user.phone_number
    },
    email: {
      title: "Email a code",
      recipient: user.email
    }
  };
  const canVerifyPhone = user.phone_number && verifyMethods.includes("sms");
  const canVerifyEmail = user.email && verifyMethods.includes("email");
  const availableMethods = [canVerifyPhone && "sms", canVerifyEmail && "email"].filter((m) => m);

  const [
    sendVerificationCodeWithRateLimit,
    { loading: sendCodeLoading },
  ] = useMutation(SEND_VERIFICATION_CODE_WITH_RATE_LIMIT);
  const [
    checkVerificationCodeWithRateLimit, { loading: checkCodeLoading },
  ] = useMutation(CHECK_VERIFICATION_CODE_WITH_RATE_LIMIT, // with rate limiting
  );

  const handleVerificationMethodSelected = (method) => {
    if (sendCodeLoading) {
      return;
    }
    if (resendDisabled) {
      return;
    }
    setMessage("");
    sendVerificationCodeWithRateLimit({
      variables: { verificationChannel: method },
    })
      .then(() => {
        startResendCountdown();
        setVerificationMethod(method);
      })
      .catch((err) => {
        const { message: errorMessage, displayMessage } = err.graphQLErrors?.[0] || {};
        setMessage(displayMessage || errorMessage);
      });
  };

  const handleVerificationCodeSubmitted = () => {
    if (checkCodeLoading) {
      return;
    }
    if (verificationCode.length !== VERIFICATION_CODE_LENGTH) {
      setMessage(PHONE_VERIFICATION_INVALID_MESSAGE);
      return;
    }
    setMessage("");
    checkVerificationCodeWithRateLimit({
      variables: { verificationChannel: verificationMethod, code: verificationCode }
    })
      .then((res) => {
        const { valid, remainingTries } = res.data?.verificationCodeCheckWithRateLimit || {};
        if (valid) {
          onVerified();
          return;
        }
        // notify last attempt
        if (remainingTries === 1) {
          setMessage(PHONE_VERIFICATION_LAST_ATTEMPT_MESSAGE);
          return;
        }
        // else case, show default message from backend
        setMessage(PHONE_VERIFICATION_INCORRECT_MESSAGE);
      })
      .catch((err) => {
        const { message: errorMessage, displayMessage } = err.graphQLErrors?.[0] || {};
        setMessage(displayMessage || errorMessage);
      });
  };

  return verificationMethod ?  (
    <>
      <VerificationCodeCheckScreen
        loading={checkCodeLoading}
        errorMessage={message}
        isMobile={isMobile}
        titleText="Enter confirmation code"
        sendToRecipient={TFA_CONFIG[verificationMethod]?.recipient}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        handleSubmit={handleVerificationCodeSubmitted}
      />
      <div className="flex space-x-2 justify-center text-green-primary">
        <button
          type="button"
          className="focus:outline-none text-sm"
          // should implement debounce timer here
          onClick={() => {
            setVerificationCode("");
            handleVerificationMethodSelected(verificationMethod);
          }}
        >
          {countdown > 0 ? `Re-send code in ${countdown}s` : `Re-send code`}
        </button>
        <span>|</span>
        <button
          type="button"
          className="focus:outline-none text-sm"
          onClick={() => {
            setVerificationCode("");
            setResendDisabled(false);
            setVerificationMethod(null);
          }}
        >
          Try another method
        </button>
      </div>
    </>
  ) : (
    <>
      <Text title className="text-xl">Let&apos;s confirm your identity</Text>
      <div className="mt-4 text-sm mx-10">Before we make any changes, we need to verify it is you</div>
      <div className="my-4">
        {
          availableMethods.map((method) => {
            return (
              <button
                key={`verify-${method}`}
                type="button"
                className="mb-px w-full focus:outline-none"
                onClick={() => handleVerificationMethodSelected(method)}
              >
                <Container paddingVertical={24}>
                  <div className="flex justify-between items-center">
                    <div className="self-center w-full">
                      <div className="font-semibold">{TFA_CONFIG[method].title}</div>
                      <div>{TFA_CONFIG[method].recipient}</div>
                    </div>
                    <div>
                      <i className="fas fa-chevron-right text-gray-secondary self-center" />
                    </div>
                  </div>
                </Container>
              </button>
            );
          })
        }
        {message && <div className="mt-2 text-red-500 text-sm">{message}</div>}
      </div>
    </>
  );
};

export default TwoFactorVerification;
